import * as React from "react";
import { isBrowser } from "react-device-detect";
import FragmentPreview from "./FragmentPreview";
import { cx } from "utils/join-classnames";
import { Modal, Dropdown, CheckBox, Tooltip, Icon } from "ui/components";
import { Fragment } from "core/models/Fragment";
import usePermissions from "ui/hooks/usePermissions";
import { VariantSetting } from "core/services/site/configValue";
import { usePublishContext } from "ui/context/PublishContext";
import useKijk from "ui/hooks/use-kijk";
import { useSessionContext } from "ui/context/SessionContext";
import styles from "./PublishModal.module.scss";
import pwa_styles from "./PublishModal_Pwa.module.scss";

interface Props {
    fragment: Fragment;
    handleClose(): void;
    handlePublish(publishOption: string, publishModule: string): void;
    publishFormats: VariantSetting[];
    download: boolean;
}
const PublishModal: React.FC<Props> = ({
    handleClose,
    handlePublish,
    fragment,
    publishFormats = [],
    download = false,
}: Props) => {
    const userPermissions = usePermissions();
    const publishContext = usePublishContext();
    const sessionContext = useSessionContext();

    const [selectedModule, setSelectedModule] = React.useState<
        string | undefined
    >(publishContext.publishOptions[0]?.key || undefined);
    const [burnSubs, setBurnSubs] = React.useState<boolean>(false);
    const [showTalpaKijk, setTalpaKijk] = React.useState<string | undefined>();
    const { description } = useKijk(showTalpaKijk, fragment.properties);

    const currentProjectId = sessionContext.selectedProject?.id || 0;

    const isOptimizedProject = [40, 46].includes(currentProjectId);

    const outputOptions = React.useMemo(() => {
        const formats = [...publishFormats];

        if (isOptimizedProject) {
            const optimizedIndex = formats.findIndex(
                (format) => format.formatkey === "optimizedmp4",
            );
            if (optimizedIndex > -1) {
                const [optimizedFormat] = formats.splice(optimizedIndex, 1);
                formats.unshift(optimizedFormat);
            }
        }

        return formats
            .filter((format) => {
                const hasSubtitleTags =
                    format.tags?.some((tag) => tag === "Subtitles") || false;
                return burnSubs ? hasSubtitleTags : !hasSubtitleTags;
            })
            .map((format) => ({
                id: format.formatkey,
                title: format.name,
                value: format.formatkey,
                iconName: undefined,
            }));
    }, [publishFormats, burnSubs, isOptimizedProject]);

    const [selectedOutput, setSelectedOutput] = React.useState<
        string | undefined
    >(outputOptions[0]?.value);

    React.useEffect(() => {
        const kijkIntegration = publishContext.accounts.find(
            (account) => account.publisherkey === "custom_talpakijkv2",
        );
        if (kijkIntegration) {
            setTalpaKijk(kijkIntegration.key);
        }
    }, [publishContext.accounts]);

    const initiatePublish = () => {
        handlePublish(selectedOutput || "", selectedModule || "");
    };

    const handleBurnSubsChange = (val: boolean): void => {
        setSelectedOutput(undefined);
        setBurnSubs(val);
    };

    const moduleOptions = publishContext.publishOptions.map((option) => ({
        id: option.key,
        title: option.name,
        value: option.key,
        iconName: undefined,
    }));

    const pwaModalContent = (
        <div className={pwa_styles.container}>
            <div className={styles.form}>
                <div>
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>
                            Select output format
                        </div>
                        <Dropdown
                            theme="dark"
                            options={outputOptions}
                            disabled={
                                !(outputOptions && outputOptions.length > 0)
                            }
                            onValueChanged={setSelectedOutput}
                            value={selectedOutput}
                            placeholderText="Select output format"
                        />
                    </div>
                    {moduleOptions &&
                        moduleOptions.length > 1 &&
                        download === false && (
                            <div className={styles.formelement}>
                                <div className={styles.formelement__caption}>
                                    Select publish location
                                </div>
                                <Dropdown
                                    theme="dark"
                                    options={moduleOptions}
                                    onValueChanged={setSelectedModule}
                                    value={selectedModule}
                                    placeholderText="Select publish location"
                                />
                            </div>
                        )}
                </div>
            </div>
        </div>
    );

    const modalContent = (
        <div className={styles.container}>
            <div className={styles.form}>
                <div className={styles.column__left}>
                    <FragmentPreview fragment={fragment} />
                </div>
                <div
                    className={cx(
                        styles.column__right,
                        styles.publish__column__right,
                    )}
                >
                    {download && userPermissions.canCreateVariants && (
                        <div className={styles.formelement}>
                            <Tooltip
                                text="No captions for this clip"
                                disabled={fragment.hasSubtitles()}
                            >
                                <CheckBox
                                    title="Burn in captions"
                                    value={burnSubs.toString()}
                                    theme="dark"
                                    onChange={handleBurnSubsChange}
                                    id="burnsubs"
                                    disabled={!fragment.hasSubtitles()}
                                />
                            </Tooltip>
                        </div>
                    )}
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>
                            Select output format
                        </div>
                        <Dropdown
                            theme="dark"
                            options={outputOptions}
                            disabled={
                                !(outputOptions && outputOptions.length > 0)
                            }
                            onValueChanged={setSelectedOutput}
                            value={selectedOutput}
                            placeholderText="Select output format"
                        />
                    </div>
                    {moduleOptions &&
                        moduleOptions.length > 1 &&
                        download === false && (
                            <div className={styles.formelement}>
                                <div className={styles.formelement__caption}>
                                    Select publish location
                                </div>
                                <Dropdown
                                    theme="dark"
                                    options={moduleOptions}
                                    onValueChanged={setSelectedModule}
                                    value={selectedModule}
                                    placeholderText="Select publish location"
                                />
                            </div>
                        )}
                    {showTalpaKijk && !description && (
                        <div className={styles.error__msg}>
                            <span>
                                <Icon
                                    name="error"
                                    className={styles.error__msg__icon}
                                />{" "}
                                Please provide the clip with a description
                                before you proceed.
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <Modal
            content={isBrowser ? modalContent : pwaModalContent}
            lineColor="amber"
            theme="dark"
            onSave={initiatePublish}
            onClose={handleClose}
            saveButtonDisabled={
                selectedOutput === undefined ||
                (!!showTalpaKijk && !description)
            }
            saveButtonText={download === false ? "Publish" : "Download"}
            title={
                download === false
                    ? `Publish "${fragment.title}"`
                    : `Download "${fragment.title}"`
            }
            icon={download === false ? "publish" : "download"}
        />
    );
};

export default PublishModal;
